/**
 * usePlanetPanel — orchestrates the planet management panel lifecycle.
 *
 * Subscribes to the `openPlanetPanel` event (emitted from GameScene's
 * E-key handler), drives the open/close handshake with the server, and
 * forwards all panel broadcast events into the Zustand store.
 *
 * Mount this hook ONCE at App level (or inside <PlanetPanel /> when
 * unmounted) so the subscriptions stay alive across snapshots.
 */

import { useEffect } from 'react';
import { onGameEvent } from '../../../core/GameEvents';
import { networkManager } from '../../../systems/NetworkManager';
import {
  usePlanetPanelStore,
  type ActivityEntryWire,
  type ScanDetailsWire,
  type StructureWire,
  type MiningStatsWire,
  type PlanetWire,
  type RichnessDiffWire,
  type HexStateWire,
} from '../state/planetPanelStore';

interface SnapshotWire {
  planet: PlanetWire;
  structures: StructureWire[];
  miningStats: Record<string, MiningStatsWire>;
  richnessDiffs: RichnessDiffWire[];
  hexState: HexStateWire[];
  activityLog: ActivityEntryWire[];
  playerCredits: number;
  orbitalScan: Array<{ lat: number; lon: number; organic: number; crystal: number }>;
  orbitalScanGrid: { latCells: number; lonCells: number };
}

/** Convenience hook: returns the store; subscribes to all server messages once. */
export function usePlanetPanel() {
  const store = usePlanetPanelStore();

  useEffect(() => {
    const unsubs: Array<() => void> = [];

    // E-key from GameScene → request panel.
    unsubs.push(
      onGameEvent('openPlanetPanel', ({ planetId }) => {
        const current = usePlanetPanelStore.getState();
        if (current.isOpen && current.planetId === planetId) return; // already open
        if (current.isOpen) {
          networkManager.closePlanetPanel();
          current.finishClose();
        }
        usePlanetPanelStore.getState().beginOpen(planetId);
        networkManager.openPlanetPanel(planetId);
      })
    );

    unsubs.push(
      onGameEvent('planetPanelSnapshot', (snapshot) => {
        usePlanetPanelStore.getState().applySnapshot(snapshot as SnapshotWire);
      })
    );

    unsubs.push(
      onGameEvent('planetPanelError', ({ reason }) => {
        usePlanetPanelStore.getState().setError(reason);
      })
    );

    unsubs.push(
      onGameEvent('planetPanelClosed', () => {
        usePlanetPanelStore.getState().finishClose();
      })
    );

    unsubs.push(
      onGameEvent('planetStructurePlanted', ({ structure, miningStats: stats }) => {
        usePlanetPanelStore.getState().upsertStructure(structure as StructureWire);
        if (stats && (structure as StructureWire).id) {
          const prev = usePlanetPanelStore.getState().miningStats;
          usePlanetPanelStore.setState({
            miningStats: { ...prev, [(structure as StructureWire).id]: stats as MiningStatsWire },
          });
        }
      })
    );

    unsubs.push(
      onGameEvent('planetStructureDismantled', ({ structureId }) => {
        usePlanetPanelStore.getState().removeStructure(structureId);
      })
    );

    unsubs.push(
      onGameEvent('planetStructureDepleted', ({ structureId }) => {
        usePlanetPanelStore.getState().markStructureDepleted(structureId);
      })
    );

    unsubs.push(
      onGameEvent('planetStructureToggled', ({ structureId, isActive }) => {
        usePlanetPanelStore.getState().setStructureActive(structureId, isActive);
      })
    );

    unsubs.push(
      onGameEvent('planetBufferUpdated', ({ outpostId, contents }) => {
        usePlanetPanelStore.getState().updateBuffer(outpostId, contents);
      })
    );

    unsubs.push(
      onGameEvent('planetActivityEntry', ({ entry }) => {
        usePlanetPanelStore.getState().prependActivity(entry as ActivityEntryWire);
      })
    );

    unsubs.push(
      onGameEvent('planetScanDetails', (details) => {
        usePlanetPanelStore.getState().setScanDetails(details as ScanDetailsWire);
      })
    );

    unsubs.push(
      onGameEvent('planetPanelCredits', ({ credits }) => {
        usePlanetPanelStore.getState().setCredits(credits);
      })
    );

    unsubs.push(
      onGameEvent('planetRichnessDiffsUpdated', ({ diffs }) => {
        usePlanetPanelStore.getState().mergeRichnessDiffs(diffs);
      })
    );

    unsubs.push(
      onGameEvent('planetHexStateUpdated', ({ hexState }) => {
        usePlanetPanelStore.getState().mergeHexState(hexState);
      })
    );

    return () => {
      for (const u of unsubs) u();
    };
  }, []);

  return store;
}
